import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircleIcon, ArrowLeftIcon, BriefcaseIcon, CheckIcon, CopyIcon, DownloadIcon, EyeIcon, Loader2Icon, MailIcon, SendIcon, PhoneIcon, Trash2Icon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { QuoteLinesEditor } from '../../components/admin/QuoteLinesEditor';
import { QuoteStatusBadge } from '../../components/admin/QuoteStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate, formatDateTime, telHref } from '../../utils/format';
import { QuoteEmailDialog } from '../../components/admin/QuoteEmailDialog';
import { QuotePdfPreview } from '../../components/admin/QuotePdfPreview';
import { downloadQuotePdf } from '../../utils/quotePdf';
import { effectiveStatus, formatKr } from '../../utils/quote';
import { quoteStatuses } from '../../types/quote';
import type { QuoteLine, QuoteStatus } from '../../types/quote';
const ease = [0.23, 1, 0.32, 1] as const;
export function QuoteDetail() {
  const {
    quoteId
  } = useParams();
  const navigate = useNavigate();
  const {
    quotes,
    cases,
    updateQuote,
    setQuoteStatus,
    duplicateQuote,
    deleteQuote,
    createCaseFromQuote,
    authLoading,
    lastWriteError,
    settings
  } = useAdminData();
  const quote = quotes.find(entry => entry.id === quoteId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [quoteDate, setQuoteDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState('');
  const [pdfPreview, setPdfPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [composing, setComposing] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!quote) return;
    setTitle(quote.title);
    setDescription(quote.description);
    setLines(quote.lines);
    setQuoteDate(quote.quoteDate);
    setValidUntil(quote.validUntil);
    setInternalNotes(quote.internalNotes);
    setCustomerNotes(quote.customerNotes);
  }, [quote?.id]);
  if (!quote) return <Navigate to="/admin/tilbud" replace data-id="element-854" data-mp="36g76ia9quu86" />;
  const status = effectiveStatus(quote);
  const linkedCase = cases.find(item => item.id === quote.caseId || item.quoteId === quote.id);
  const locked = quote.status === 'Accepteret';
  const history = [...(quote.history ?? [])].sort((a, b) => b.at.localeCompare(a.at));
  async function handleSave() {
    if (saving) return;
    // Der skrives aldrig, mens Firebase stadig gendanner sessionen.
    if (authLoading) {
      setError('Sessionen indlæses stadig. Prøv igen om et øjeblik.');
      return;
    }
    if (locked) {
      const confirmed = window.confirm('Tilbuddet er accepteret af kunden. Er du sikker på, at du vil ændre pris og indhold?');
      if (!confirmed) return;
    }
    setSaving(true);
    setError('');
    const result = await updateQuote(quote.id, {
      title,
      description,
      lines,
      quoteDate,
      validUntil,
      internalNotes,
      customerNotes
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error ?? 'Ændringerne kunne ikke gemmes.');
      return;
    }
    setSaved(true);
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-855" data-mp="r1c65ult482s">
      <Link to="/admin/tilbud" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-856" data-mp="qziyqd3ddjtq">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-857" data-mp="gj6xzvcpdvkc" />
        Tilbud
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-858" data-mp="2j610qzk78a5">
        <div data-id="element-859" data-mp="6jpspf1d5jow">
          <p className="text-sm text-ink-muted" data-id="element-860" data-mp="6j7qm1s0sb92">{quote.quoteNumber}</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-861" data-mp="u9k55vhrt68m">
            {quote.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-862" data-mp="z7vfs8fw3krw">
            {quote.customerName} · {formatKr(quote.total)} inkl. moms
          </p>
        </div>
        <div className="flex flex-col items-start gap-3 sm:items-end" data-id="element-863" data-mp="pibznajka50q">
          <QuoteStatusBadge status={status} data-id="element-864" data-mp="1d8fzd6tmpkmg" />
          <SelectField id="tilbud-status" label="Status" className="w-52" value={quote.status} onChange={async event => {
          setWorking('status');
          setError('');
          const result = await setQuoteStatus(quote.id, event.target.value as QuoteStatus);
          setWorking('');
          if (!result.ok) {
            setError(result.error ?? 'Statussen kunne ikke gemmes. Prøv igen.');
          }
        }} options={quoteStatuses.filter(option => option !== 'Udløbet').map(option => ({
          value: option,
          label: option
        }))} data-id="element-865" data-mp="1d7d7owohfe5h" />
        </div>
      </div>

      {sentTo && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-866" data-mp="2etpd5dhnr8jo">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-867" data-mp="2oozes0459dwx">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-868" data-mp="8wtfqrfsecya" />
          </span>
          Tilbuddet er sendt til {sentTo}
        </p>}

      {status === 'Udløbet' && <p className="mt-5 flex items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-869" data-mp="b1ycqom1shs0">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-870" data-mp="fzzzbjiomz88" />
          Tilbuddet er udløbet ({formatDate(quote.validUntil)}). Forlæng gyldigheden, eller lav en kopi.
        </p>}

      {quote.status === 'Accepteret' && <section className="mt-5 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-871" data-mp="3oipcljd4ktc5">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-872" data-mp="3tfxwbodx95fi">
            <div data-id="element-873" data-mp="2aprmfp06zbh">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-874" data-mp="2a66yr7yxwtr">
                {linkedCase ? 'Sag allerede oprettet' : 'Tilbuddet er accepteret'}
              </h2>
              <p className="mt-1 text-[15px] text-ink-soft" data-id="element-875" data-mp="35rof6zrnl5up">
                {linkedCase ? `Sag #${linkedCase.number} — ${linkedCase.title}` : 'Opret sagen, så arbejdet kan planlægges og udføres.'}
              </p>
            </div>
            {linkedCase ? <Link to={`/admin/sager/${linkedCase.id}`} className={buttonClasses('secondary', 'md')} data-id="element-876" data-mp="1nt06sed2z98b">
                <BriefcaseIcon className="h-4 w-4" aria-hidden="true" data-id="element-877" data-mp="1itx9xh7989bf" />
                Åbn sag
              </Link> : <Button size="md" disabled={working === 'case'} onClick={async () => {
          setWorking('case');
          setError('');
          const created = await createCaseFromQuote(quote);
          setWorking('');
          if (!created) {
            setError('Sagen kunne ikke oprettes. Prøv igen.');
            return;
          }
          navigate(`/admin/sager/${created.id}`);
        }} data-id="element-878" data-mp="1duu62030g951">
                {working === 'case' ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-879" data-mp="l7gg4lxqcduf" />
                    Opretter …
                  </> : 'Opret sag fra tilbud'}
              </Button>}
          </div>
        </section>}

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.5fr_0.5fr]" data-id="element-880" data-mp="2026akzrb4ixs">
        <div className="space-y-5" data-id="element-881" data-mp="250zqszxzao1n">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-882" data-mp="24zy4u0ffch55">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-883" data-mp="1qc3aygpok6qh">Opgave</h2>
            {locked && <p className="mt-3 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-884" data-mp="1v4wgh6sz3j3m">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-885" data-mp="1hadkpe7jbdua" />
                Tilbuddet er accepteret. Ændringer i pris og indhold skal bekræftes, før de gemmes.
              </p>}
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-886" data-mp="1ccu5oc30ix1a">
              <TextField id="td-titel" label="Tilbudstitel" className="sm:col-span-2" value={title} onChange={event => {
              setTitle(event.target.value);
              setSaved(false);
            }} data-id="element-887" data-mp="1ccc4r25enun1" />
              <TextField id="td-dato" label="Tilbudsdato" type="date" value={quoteDate} onChange={event => {
              setQuoteDate(event.target.value);
              setSaved(false);
            }} data-id="element-888" data-mp="12n56e4ixise3" />
              <TextField id="td-gyldig" label="Gyldig til" type="date" value={validUntil} onChange={event => {
              setValidUntil(event.target.value);
              setSaved(false);
            }} data-id="element-889" data-mp="1lfptf5by639z" />
            </div>
            <TextArea id="td-beskrivelse" label="Beskrivelse af arbejdet" className="mt-5" rows={4} value={description} onChange={event => {
            setDescription(event.target.value);
            setSaved(false);
          }} data-id="element-890" data-mp="1qg55ltkht4qd" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-891" data-mp="3oharyp76j8qv">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-892" data-mp="3of7rfb8ehx7w">Tilbudslinjer</h2>
            <div className="mt-5" data-id="element-893" data-mp="p02xwglfxwfv">
              <QuoteLinesEditor lines={lines} onChange={next => {
              setLines(next);
              setSaved(false);
            }} data-id="element-894" data-mp="k2kpqsf8zl8x" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-895" data-mp="b1w9dr2dt8sv">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-896" data-mp="30srgy1wqwhv3">Noter</h2>
            <TextArea id="td-kundenoter" label="Bemærkninger til kunden" className="mt-5" rows={3} value={customerNotes} onChange={event => {
            setCustomerNotes(event.target.value);
            setSaved(false);
          }} data-id="element-897" data-mp="2vsdhv9d4gi97" />
            <TextArea id="td-internenoter" label="Interne noter" className="mt-5" rows={3} value={internalNotes} onChange={event => {
            setInternalNotes(event.target.value);
            setSaved(false);
          }} data-id="element-898" data-mp="1k969lxgn41ax" />
          </section>

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-899" data-mp="23zrspa5x5rzm">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-900" data-mp="27zk4rwuiug9q" />
              {error}
            </p>}

          <div className="flex flex-wrap items-center gap-3" data-id="element-901" data-mp="9nlgq957b769">
            <Button size="lg" onClick={handleSave} disabled={saving} data-id="element-902" data-mp="dnlvjuw7pkdu">
              {saving ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-903" data-mp="1gxzyn526gc8r" />
                  Gemmer …
                </> : 'Gem'}
            </Button>
            <Button variant="secondary" size="lg" onClick={() => setPdfPreview(true)} data-id="element-904" data-mp="1pwblqx3u4rwg">
              <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-905" data-mp="1lvjc42r1wkzn" />
              Forhåndsvis PDF
            </Button>
            <Button variant="secondary" size="lg" disabled={downloading} onClick={async () => {
            setDownloading(true);
            setError('');
            try {
              await downloadQuotePdf(quote, settings);
            } catch {
              setError('PDF’en kunne ikke dannes. Prøv igen.');
            }
            setDownloading(false);
          }} data-id="element-906" data-mp="1zuj0d9anixva">
              {downloading ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-907" data-mp="2x9akexko1bh7" />
                  Danner PDF …
                </> : <>
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-908" data-mp="326j3r1hjvlk1" />
                  Download PDF
                </>}
            </Button>
            <Button variant="dark" size="lg" onClick={() => setComposing(true)} data-id="element-909" data-mp="2s9ee2rohx7yh">
              <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-910" data-mp="2ej1at2mjuhfh" />
              Send tilbud på mail
            </Button>
            {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-911" data-mp="2jc45je32bwx9">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-912" data-mp="24my55msho0ns">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-913" data-mp="1vkgij78x9609" />
                </span>
                Ændringerne er gemt
              </p>}
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-914" data-mp="20lmdzctyv7dh">
            <Button variant="secondary" size="md" disabled={working === 'accept' || quote.status === 'Accepteret'} onClick={async () => {
            setWorking('accept');
            setError('');
            const result = await setQuoteStatus(quote.id, 'Accepteret');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-915" data-mp="1shskxsl8mvkl">
              Marker som accepteret
            </Button>
            <Button variant="secondary" size="md" disabled={working === 'reject' || quote.status === 'Afvist'} onClick={async () => {
            setWorking('reject');
            setError('');
            const result = await setQuoteStatus(quote.id, 'Afvist');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-916" data-mp="14vc8idq0v655">
              Marker som afvist
            </Button>
            <button type="button" onClick={() => setConfirmingDelete(true)} className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-line px-4 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-917" data-mp="gxec88f5ksfj">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-918" data-mp="3p6xr9nd8kzz4" />
              Slet tilbud
            </button>
          </div>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-919" data-mp="3u6apf49k5th0">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-920" data-mp="1zc73tsh3yw6">Historik</h2>
            <ol className="mt-4 space-y-3" data-id="element-921" data-mp="71b9b0ch8prj">
              {history.map(event => <li key={event.id} className="flex gap-3 text-[15px]" data-id="element-922" data-mp="1df0vmpldl5z3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" data-id="element-923" data-mp="1iedphuanzdb6" />
                  <span data-id="element-924" data-mp="3ejwd5m46xbuc">
                    <span className="text-ink" data-id="element-925" data-mp="3agznxck5ahhf">{event.text}</span>
                    <span className="block text-sm text-ink-muted" data-id="element-926" data-mp="3aiswwyi8ddsl">
                      {formatDateTime(event.at)}
                      {event.by ? ` · ${event.by}` : ''}
                    </span>
                  </span>
                </li>)}
              {history.length === 0 && <li className="text-sm text-ink-muted" data-id="element-927" data-mp="2vp7jhvlw2nuh">Ingen aktivitet registreret endnu.</li>}
            </ol>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-928" data-mp="35i56adxwyyyw">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-929" data-mp="35j6l8ti1w5lw">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-930" data-mp="30pc1xr6cxsnc">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-931" data-mp="2cz81fsogu90k">
              {quote.customerName || '—'}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-932" data-mp="njf680tnpnmn">
              <li data-id="element-933" data-mp="ni40hvgni5sw">{quote.customerPhone || '—'}</li>
              <li className="break-all" data-id="element-934" data-mp="sbp17v9mpdvs">{quote.customerEmail || '—'}</li>
              <li data-id="element-935" data-mp="ejsfqa2gu28d">{quote.customerAddress || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-936" data-mp="ehfn92z309h2">
              {quote.customerPhone && <LinkButton href={telHref(quote.customerPhone)} variant="secondary" size="sm" data-id="element-937" data-mp="16af09gg0dnms">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-938" data-mp="168loxnugaf8g" />
                  Ring
                </LinkButton>}
              {quote.customerEmail && <LinkButton href={`mailto:${quote.customerEmail}`} variant="secondary" size="sm" data-id="element-939" data-mp="x9grkkumkrxv">
                  <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-940" data-mp="1270avz66juwo" />
                  Send e-mail
                </LinkButton>}
            </div>
            {quote.customerId && <Link to={`/admin/kunder/${quote.customerId}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-941" data-mp="3n0dih94i7z31">
                Se kundeprofil
              </Link>}
          </section>

          {linkedCase && <section className="rounded-card border border-line bg-white p-6" data-id="element-942" data-mp="8l5wvh7xnm8v">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-943" data-mp="3ljk18w0o80l">
                Tilknyttet sag
              </h2>
              <p className="mt-3 text-[15px] text-ink" data-id="element-944" data-mp="w8nv9vlpdikt">
                Sag #{linkedCase.number} — {linkedCase.title}
              </p>
              <Link to={`/admin/sager/${linkedCase.id}`} className={`${buttonClasses('secondary', 'sm')} mt-4`} data-id="element-945" data-mp="rgw17b3c8upx">
                Åbn sag
              </Link>
            </section>}

          <section className="rounded-card border border-line bg-white p-6" data-id="element-946" data-mp="1k0bd7o0zljay">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-947" data-mp="1f5xdxaj7es10">Beløb</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-948" data-mp="1mk7pm3owqujp">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-949" data-mp="1rgy5apwsvo4l">
                <dt className="text-ink-soft" data-id="element-950" data-mp="1wgu9ap5hzfk5">Ekskl. moms</dt>
                <dd className="font-medium text-ink" data-id="element-951" data-mp="1wf9fj1r0c0nc">{formatKr(quote.subtotal)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-952" data-mp="21h8gk4z9ylh4">
                <dt className="text-ink-soft" data-id="element-953" data-mp="25ghab9ubejco">Moms</dt>
                <dd className="font-medium text-ink" data-id="element-954" data-mp="25fo9sqly8q2z">{formatKr(quote.vatAmount)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-955" data-mp="2f7bxqhjarozp">
                <dt className="font-medium text-ink" data-id="element-956" data-mp="2f7tztusn4xfj">Total</dt>
                <dd className="font-display text-lg font-bold text-ink" data-id="element-957" data-mp="2k6oimwqlylzn">{formatKr(quote.total)}</dd>
              </div>
            </dl>
            {quote.lastSentAt ? <p className="mt-4 text-sm text-ink-muted" data-id="element-958" data-mp="2t737g62s6j7w">
                Sendt til {quote.sentTo} den {formatDateTime(quote.lastSentAt)}
              </p> : <p className="mt-4 text-sm text-ink-muted" data-id="element-959" data-mp="3spbcfnfihwh4">Tilbuddet er endnu ikke sendt til kunden.</p>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-960" data-mp="6g8ovekpgbkp">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-961" data-mp="6id1qm0l0kx4">Handlinger</h2>
            <div className="mt-4 flex flex-col gap-2" data-id="element-962" data-mp="fgor6kpppcnz">
              <Button variant="secondary" size="sm" disabled={working === 'copy'} onClick={async () => {
              setWorking('copy');
              setError('');
              const copy = await duplicateQuote(quote);
              setWorking('');
              if (!copy) {
                setError(lastWriteError() || 'Tilbuddet kunne ikke duplikeres. Prøv igen.');
                return;
              }
              navigate(`/admin/tilbud/${copy.id}`);
            }} data-id="element-963" data-mp="ffvphy5m1dcp">
                <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-964" data-mp="2np3dkjkhh0g5" />
                Dupliker tilbud
              </Button>
            </div>
          </section>
        </aside>
      </div>

      {pdfPreview && <QuotePdfPreview quote={quote} onClose={() => setPdfPreview(false)} data-id="element-965" data-mp="2eoex7i7mao03" />}

      {composing && <QuoteEmailDialog quote={quote} onClose={() => setComposing(false)} onSent={email => {
      setComposing(false);
      setSentTo(email);
    }} data-id="element-966" data-mp="29pu6c9e99gob" />}

      {confirmingDelete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-tilbud-titel" data-id="element-967" data-mp="2xmhrocixpd5a">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-968" data-mp="3q2itiv1ogo8b">
            <h2 id="slet-tilbud-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-969" data-mp="83gfi7dmyw5d">
              Er du sikker på, at du vil slette dette tilbud?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-970" data-mp="3vf7ptxuf4zvh">
              {quote.quoteNumber} slettes permanent.
            </p>
            {linkedCase && <p className="mt-4 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-971" data-mp="3li2xthdw6a6r">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-972" data-mp="3qh64t7dfmqhd" />
                Tilbuddet er knyttet til sag #{linkedCase.number}. Sagen bliver ikke slettet, men mister
                henvisningen til tilbuddet.
              </p>}
            {error && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-973" data-mp="1apia7ilw5a1q">
                {error}
              </p>}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-974" data-mp="1eq28wqm44lpz">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setError('');
            const result = await deleteQuote(quote.id);
            setDeleting(false);
            if (!result.ok) {
              setError(result.error ?? 'Tilbuddet kunne ikke slettes. Prøv igen.');
              return;
            }
            navigate('/admin/tilbud');
          }} data-id="element-975" data-mp="2z8mho5oqqgwu">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-976" data-mp="3rxa6uccm1qfo" />
                    Sletter …
                  </> : 'Slet tilbud'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirmingDelete(false)} data-id="element-977" data-mp="1skz2oo1mgmg9">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}
    </div>;
}